import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, combineLatest, firstValueFrom, distinctUntilChanged, Observable, of, switchMap, map, tap, BehaviorSubject, Subscription, forkJoin, from } from 'rxjs';
import { filter, catchError, shareReplay, mergeMap, toArray } from 'rxjs/operators';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TransactionReceipt, formatEther, parseEther } from 'viem';
import { StateService } from '../../services/state.service';
import { Web3Service } from '../../services/web3.service';
import { TransactionTrackingService } from '../../services/transaction-tracking.service';
import { ThemeService } from '../../services/theme.service';
import { BidTagsService } from '../../services/bid-tags.service';
import { NotConnectedComponent } from '../not-connected/not-connected.component';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { DataService } from '@/services/data.service';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { TippyDirective } from '@/directives/tippy.directive';
import { FormsModule } from '@angular/forms';
import { StashBidsTableComponent } from '../stash-bids-table/stash-bids-table.component';
import { SelectPunkModalComponent } from '../select-punk-modal/select-punk-modal.component';
import { PlatformService } from '../../services/platform.service';
import { QrCodeComponent } from 'ng-qrcode';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import {
  nativeBidTypeLabel,
  onChainBidCancelFailure,
  onChainBidCancelSuccess,
  onChainBidTypeLabel,
} from '@/utils/native-market-bid-labels';
import { PunksmarketV1Service } from '@/services/punksmarket-v1.service';
import {
  mapPunksmarketIndexerBidToUnified,
  unifiedBidToStashTableShape,
} from '@/utils/unified-market-bid.mappers';
import { classifyPunksmarketIndexerBid } from '@/utils/punksmarket-criteria.util';

interface PunkBid {
  id: string;
  additionalBidIds?: string[]; // Extra bid IDs merged when duplicates are grouped
  bidder_address: string;
  punk_indices?: string;
  punk_indices_count?: number;
  punkIndices?: number[];
  selected_punk_ids?: number[] | string; // For targeted bids
  // tag?: string;
  tags?: any;
  bid_amount_wei: string;
  bid_amount_eth: number;
  merkle_root: string;
  account_nonce: number;
  bid_nonce: number;
  status: string;
  created_at: string;
  expires_at?: string;
  signature: string;
  source?: string; // Kept for backward compatibility
  sources?: string[]; // Array of sources for grouped bids
  decodedTags?: string[];
  bidType?: 'trait' | 'collection' | 'custom' | 'owner';
  bidTypeLabel?: string;
  ownerAddress?: string; // Extracted owner address for owner bids
  confidential?: boolean; // Whether this bid is confidential
  isConfidential?: boolean; // Computed flag for UI display
  /** API may send boolean or SQLite-style 0/1; UI normalizes to 0 | 1. */
  stash_funded?: boolean | number | 0 | 1;
  // Precomputed properties for template performance
  formattedExpiresAt?: string | null; // Precomputed formatted date
  formattedPlatform?: string | null; // Precomputed formatted platform name
  isDelegated?: boolean; // Whether this bid is from a delegated wallet
  cancelButtonTooltipOptions?: { content: string; placement: 'top' }; // Tooltip options for cancel button (only set if delegated)
  // Incoming-only: used by stash bids (matchingOwnedPunks) and legacy bids (isLegacyBid)
  isLegacyBid?: boolean;
  matchingOwnedPunks?: number[];
  matching_owned_punks?: number[];
  matching_owned_punks_count?: number;
  punksmarketBidId?: string;
  platform?: string;
}

/** Incoming list can contain both PunkBid (stash) and legacy bid objects (from owned punks). */
type IncomingBid = (PunkBid | { id: string; bidder_address: string; bid_amount_eth?: number; bidType?: string; isLegacyBid?: boolean; punk_id?: number; matchingOwnedPunks?: number[]; punkIndices?: number[]; [key: string]: any }) & { punksForFrise?: number[] };

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, NotConnectedComponent, RouterModule, LazyLoadImageModule, TokenIdParsePipe, WalletAddressDirective, TippyDirective, SelectPunkModalComponent, QrCodeComponent],
  selector: 'app-my-bids',
  templateUrl: './my-bids.component.html',
  styleUrls: ['./my-bids.component.scss']
})
export class MyBidsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private incomingBidsExecutionSub?: Subscription;
  
  isConnected = false;
  walletAddress: string | null = null;
  bids: PunkBid[] = [];
  loading = false;
  error: string | null = null;
  
  // Tab management
  activeTab: 'outgoing' | 'incoming' = 'outgoing';
  
  // Incoming bids
  incomingBids$: Observable<IncomingBid[]> = of([]);
  loadingIncomingBids = false;
  errorIncomingBids: string | null = null;
  ownedPunkIds: number[] = [];
  
  // Stash properties
  stashAddress: string | null = null;
  stashBalanceWei: bigint | null = null;
  stashBalanceEth: number = 0;
  loadingStash: boolean = false;
  withdrawing: boolean = false;
  depositing: boolean = false;
  
  // Deposit modal properties
  depositModalActive: boolean = false;
  depositAmount: string = '';
  
  // Withdraw modal properties
  withdrawModalActive: boolean = false;
  withdrawAmount: string = '';
  
  // Modal states
  cancelBidConfirmationModalActive = false;
  selectedBidForCancellation: PunkBid | null = null;
  isMyBidsTransactionActive = false;
  cancelAllBidsConfirmationModalActive = false;
  
  // Properties for punk selection modal
  selectedStashBid: any | null = null;
  matchingPunkIdsForBid: number[] = [];
  selectPunkForBidModalActive: boolean = false;

  // Email modal properties
  emailModalActive: boolean = false;
  userEmail: string = '';
  loadingEmail: boolean = false;

  // Notifications dropdown
  notificationsMenuOpen: boolean = false;
  headerActionsMenuOpen = false;

  // Telegram linking
  telegramLinkCode: string = '';
  loadingTelegramLink: boolean = false;
  loadingTelegramUnlink: boolean = false;
  telegramLinked: boolean = false;
  telegramLinkError: string | null = null;

  // Settings modal properties
  settingsModalActive: boolean = false;
  displayOnlyHigherBidPerToken: boolean = true;
  private displayOnlyHigherBidPerToken$ = new BehaviorSubject<boolean>(true);
  private readonly STORAGE_KEY = 'my-bids-display-only-higher-bid-per-token';

  displayOnlyBidsAboveMinEth: boolean = true;
  private displayOnlyBidsAboveMinEth$ = new BehaviorSubject<boolean>(true);
  private readonly STORAGE_KEY_MIN_ETH = 'my-bids-display-only-bids-above-min-eth';
  /** Minimum bid ETH threshold from config (per collection). Used for "display only bids above X ETH" toggle. */
  minBidEthConfig: number;

  // Precomputed properties for template performance (avoid method calls in template)
  formattedStashBalance: string = '0';
  hasStashOutgoingBids = false;
  stashEtherscanUrl: string = '';
  canWithdrawFlag: boolean = false;
  canDepositFlag: boolean = false;
  isUsingDelegatedWallet: boolean = false;
  acceptButtonTooltipOptions?: { content: string; placement: 'top' };
  /** Tooltip for Accept button when the bid is from the same wallet (own bid). */
  readonly acceptSameWalletTooltipOptions: { content: string; placement: 'top' } = {
    content: 'Your own bid',
    placement: 'top'
  };

  protected readonly environment = environment;

  readonly showIncomingBidsTab =
    environment.featureFlags?.STASH_BID_SUPPORT !== false ||
    (environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false);

  constructor(
    private stateService: StateService,
    private web3Service: Web3Service,
    private http: HttpClient,
    public txTrackingSvc: TransactionTrackingService,
    public themeSvc: ThemeService,
    private bidTagsService: BidTagsService,
    public dataSvc: DataService,
    private router: Router,
    private platformService: PlatformService,
    private punksmarketV1Svc: PunksmarketV1Service,
  ) {
    this.minBidEthConfig = environment.myBidsMinEth ?? 1;
  }

  ngOnInit(): void {
    // Restore display settings from localStorage
    this.restoreDisplayOnlyHigherBidPerTokenState();
    this.restoreDisplayOnlyBidsAboveMinEthState();

    // Subscribe to wallet connection state
    // This will reload bids whenever the user connects (even if same address)
    combineLatest([
      this.stateService.web3Connected$,
      this.stateService.walletAddress$,
      this.stateService.authenticatedAsAddress$
    ]).pipe(
      distinctUntilChanged((prev, curr) => 
        prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2]
      ),
      takeUntil(this.destroy$)
    ).subscribe(([isConnected, walletAddress, authenticatedAsAddress]) => {
      this.isConnected = isConnected;
      this.walletAddress = walletAddress;
      this.isUsingDelegatedWallet = !!(
        isConnected &&
        walletAddress &&
        authenticatedAsAddress &&
        walletAddress.toLowerCase() !== authenticatedAsAddress.toLowerCase()
      );
      this.acceptButtonTooltipOptions = this.isUsingDelegatedWallet ? {
        content: 'You are currently using a delegated wallet. Switch to the connected wallet to accept this bid.',
        placement: 'top'
      } : undefined;
      
      if (isConnected && walletAddress) {
        const activeIdentity = this.getActiveIdentityAddress(walletAddress, authenticatedAsAddress);
        if (!activeIdentity) {
          return;
        }
        // Reload bids and stash info when user connects
        // This ensures bids are refreshed every time the user connects
        this.loadBids();
        this.loadStashInfo();
        this.initializeIncomingBids(activeIdentity);
        this.loadEmail();
      } else {
        // Clear data when user disconnects
        this.bids = [];
        this.stashAddress = null;
        this.stashBalanceWei = null;
        this.stashBalanceEth = 0;
        this.ownedPunkIds = [];
        this.incomingBids$ = of([]);
        this.isUsingDelegatedWallet = false;
        this.acceptButtonTooltipOptions = undefined;
        this.updateComputedProperties();
      }
    });

    // Reload bids when the menu opens
    this.stateService.menuOpen$.pipe(
      distinctUntilChanged(),
      filter(isOpen => isOpen === true),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.isConnected && this.walletAddress) {
        const activeIdentity = this.getActiveIdentityAddress();
        if (!activeIdentity) {
          return;
        }
        this.loadBids();
        this.loadStashInfo();
        this.initializeIncomingBids(activeIdentity);
      }
    });

    this.stateService.stashWithdrawalRequested$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleStashWithdrawalRequest();
    });

    this.stateService.outgoingBidsRefreshRequested$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.isConnected && this.walletAddress) {
        void this.loadBids();
        void this.loadStashInfo();
        const activeIdentity = this.getActiveIdentityAddress();
        if (activeIdentity) {
          this.initializeIncomingBids(activeIdentity);
        }
      }
    });

  }

  private async handleStashWithdrawalRequest(): Promise<void> {
    if (!this.isConnected || !this.walletAddress) {
      return;
    }

    if (!this.stashAddress || this.stashBalanceWei === null) {
      await this.loadStashInfo();
    }

    this.withdrawFromStash();
  }
  
  private initializeIncomingBids(walletAddress: string): void {
    // Cancel previous incoming-bids pipeline to avoid stale responses overriding mimic state.
    this.incomingBidsExecutionSub?.unsubscribe();

    //walletAddress = '0x696ed7b26f4b019ceec78dc8b9140ad64a6f354b'
    this.loadingIncomingBids = true;
    this.errorIncomingBids = null;
    
    console.log('[MyBids] Initializing incoming bids for address:', walletAddress);
    
    // Create the observable for incoming bids (server-side min ETH filter via displayOnlyBidsAboveMinEth$).
    // Re-trigger is handled by the combineLatest in ngOnInit which re-calls
    // initializeIncomingBids whenever walletAddress$ or authenticatedAsAddress$ change (including mimic).
    const owned$ = this.dataSvc.fetchOwned(walletAddress.toLowerCase()).pipe(
      tap(() => {
        console.log('[MyBids] Fetching owned punks for incoming bids...', walletAddress);
      }),
      shareReplay(1)
    );

    const incomingBidsObservable = combineLatest([
      owned$,
      this.displayOnlyBidsAboveMinEth$
    ]).pipe(
      switchMap(([owned, filterMinEth]) => {
        console.log('[MyBids] Owned punks:', owned.length);
        if (owned.length === 0) {
          this.ownedPunkIds = [];
          this.loadingIncomingBids = false;
          return of([]);
        }
        
        // Extract punk IDs from owned punks
        this.ownedPunkIds = owned.map(punk => parseInt(punk.id)).filter(id => !isNaN(id));
        console.log('[MyBids] Owned punk IDs:', this.ownedPunkIds.length);
        
        // Extract legacy bids from owned punks (filter by min ETH client-side when toggle is on)
        let legacyBids = owned
          .filter(punk => punk.bid?.value && punk.bid?.fromAccount?.id)
          .map(punk => {
            const bid = punk.bid!;
            const punkId = parseInt(punk.id);
            return {
              id: `legacy-${punk.id}`,
              bidder_address: bid.fromAccount!.id,
              bid_amount_wei: bid.value,
              bid_amount_eth: parseFloat(this.web3Service.weiToEth(bid.value)),
              bidType: 'legacy' as const,
              bidTypeLabel: nativeBidTypeLabel,
              isLegacyBid: true,
              stash_funded: 1,
              expires_at: null,
              created_at: bid.blockTimestamp,
              legacyBid: bid,
              punk_id: punkId,
              matchingOwnedPunks: [punkId],
              punksForFrise: [punkId]
            };
          });
        if (filterMinEth) {
          legacyBids = legacyBids.filter((b) => (b.bid_amount_eth ?? 0) >= this.minBidEthConfig);
        }
        console.log('[MyBids] Legacy bids found:', legacyBids.length);
        
        if (this.ownedPunkIds.length === 0) {
          this.loadingIncomingBids = false;
          return of(legacyBids);
        }

        const stash$ =
          environment.featureFlags?.STASH_BID_SUPPORT !== false
            ? this.dataSvc.fetchAllStashBids({
                filterPunkIds: this.ownedPunkIds,
                filterAmountMin: filterMinEth ? this.minBidEthConfig : undefined,
              })
            : of({ bids: [] as any[] });

        const punksmarket$ = this.fetchIncomingPunksmarketBids$(this.ownedPunkIds);

        return forkJoin({ stash: stash$, punksmarket: punksmarket$ }).pipe(
          tap((response) => {
            console.log('[MyBids] Fetched incoming stash response:', response.stash);
          }),
          map(({ stash, punksmarket }) => {
            this.loadingIncomingBids = false;
            this.errorIncomingBids = null;
            const stashBids = stash.bids || [];
            console.log('[MyBids] Processed stash bids:', stashBids.length);

            const processedStashBids = stashBids.map((bid: any) => {
              const decodedTags = this.bidTagsService.decodeCollectionBidTags(bid);
              const bidType = this.bidTagsService.getCollectionBidType(bid, decodedTags);
              
              // Extract owner address for owner bids
              let ownerAddress: string | undefined;
              if (bidType === 'owner' && decodedTags.length > 0) {
                const ownerTag = decodedTags[0];
                const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
                if (match) {
                  ownerAddress = match[1].toLowerCase();
                }
              }
              
              // Extract punk indices for custom bid preview (same as outgoing; API returns first 3 + count)
              let punkIndices: number[] = [];
              if (bid.punk_indices) {
                try {
                  punkIndices = JSON.parse(bid.punk_indices);
                } catch {
                  punkIndices = [];
                }
              } else if (bid.selected_punk_ids) {
                try {
                  if (typeof bid.selected_punk_ids === 'string') {
                    punkIndices = JSON.parse(bid.selected_punk_ids);
                  } else if (Array.isArray(bid.selected_punk_ids)) {
                    punkIndices = bid.selected_punk_ids;
                  }
                } catch {
                  punkIndices = [];
                }
              }
              
              // Prefer server-computed matching (uses full indices), fallback to client computation.
              const serverMatchingOwnedPunks = Array.isArray((bid as any).matching_owned_punks)
                ? (bid as any).matching_owned_punks
                    .map((id: any) => Number(id))
                    .filter((id: number) => Number.isInteger(id) && id >= 0)
                : null;
              const matchingOwnedPunks = serverMatchingOwnedPunks ?? this.calculateMatchingOwnedPunks(bid, bidType);
              const punkIndicesCount = bid.punk_indices_count != null ? Number(bid.punk_indices_count) : punkIndices.length;
              // For frise: show matching punks if any, otherwise all owned (bids were filtered by owned so they apply to them)
              const punksForFrise = (matchingOwnedPunks && matchingOwnedPunks.length > 0)
                ? matchingOwnedPunks
                : (this.ownedPunkIds.length > 0 ? [...this.ownedPunkIds] : []);
              
              return {
                ...bid,
                decodedTags,
                bidType,
                punkIndices,
                punk_indices_count: punkIndicesCount,
                bidTypeLabel: bidType === 'owner' ? 'Owner Bid' : 
                             bidType === 'custom' ? 'Custom Bid' : 
                             bidType === 'trait' ? 'Trait Bid' : 
                             'Collection Bid',
                ownerAddress,
                confidential: !!(bid.confidential === true || (typeof bid.confidential === 'string' && bid.confidential === 'true') || (typeof bid.confidential === 'number' && bid.confidential === 1)),
                stash_funded: normalizeStashFundedForUi(bid.stash_funded),
                matchingOwnedPunks,
                punksForFrise
              };
            });
            
            const groupedStashBids = this.groupDuplicateBids(processedStashBids);
            const allBids = [...groupedStashBids, ...punksmarket, ...legacyBids];
            console.log(
              '[MyBids] Total incoming bids (stash + on-chain + legacy):',
              allBids.length,
            );
            
            // Sort by bid amount descending (highest first)
            const sortedBids = allBids.sort((a, b) => {
              const amountA = Number(a.bid_amount_eth || 0);
              const amountB = Number(b.bid_amount_eth || 0);
              return amountB - amountA;
            });
            
            return sortedBids;
          }),
          catchError((error) => {
            console.error('[MyBids] Error loading incoming bids:', error);
            this.loadingIncomingBids = false;
            this.errorIncomingBids = 'Failed to load incoming bids. Please try again later.';
            return of([]);
          })
        );
      }),
      tap((bids) => {
        console.log('[MyBids] Final incoming bids count:', bids.length);
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );
    
    // Combine with "highest per token" toggle only (min ETH filter is applied server-side)
    this.incomingBids$ = combineLatest([
      incomingBidsObservable,
      this.displayOnlyHigherBidPerToken$
    ]).pipe(
      map(([bids, shouldFilterHighestPerToken]) => {
        if (shouldFilterHighestPerToken) {
          return this.filterBidsByHighestPerToken(bids);
        }
        return bids;
      }),
      takeUntil(this.destroy$)
    );
    
    // Keep a single eager subscription so data can preload before opening the incoming tab.
    this.incomingBidsExecutionSub = incomingBidsObservable.subscribe({
      next: (bids) => {
        console.log('[MyBids] Subscription received bids:', bids.length);
      },
      error: (error) => {
        console.error('[MyBids] Subscription error:', error);
        this.loadingIncomingBids = false;
        this.errorIncomingBids = 'Failed to load incoming bids. Please try again later.';
      }
    });
  }

  ngOnDestroy(): void {
    this.incomingBidsExecutionSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeSidebar(): void {
    this.stateService.setMenuOpen(false);
  }

  async loadBids(): Promise<void> {
    if (!this.walletAddress) return;

    this.loading = true;
    this.error = null;

    try {
      // Get all wallet addresses (connected wallet + delegations)
      const wallets = this.getAllWalletAddresses();
      
      if (wallets.length === 0) {
        this.bids = [];
        this.loading = false;
        return;
      }

      // Extract base URL from alternativeListingsGraphURI
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      // Determine source filter based on environment collection
      const getBidSource = (collection: string): string => {
        if (collection === 'cryptopunks') return 'cryptopunks.eth.limo,cryptopunks.app';
        if (collection === 'cryptopunks-v1') return 'v1cryptopunks.com';
        return 'cryptopunks.eth.limo'; // fallback
      };
      const filterSource = getBidSource(environment.collection);
      
      // Build query parameters (server-side min amount filter when toggle is on)
      const params = wallets.map(wallet => `wallets=${encodeURIComponent(wallet)}`).join('&');
      const url = `${baseUrl}/api/scraper/punk-bids/by-bidders?${params}&filterSource=${encodeURIComponent(filterSource)}`;

      // Prepare headers (admin JWT + mimic-as for confidential bid visibility)
      const jwtToken = this.stateService.getJwtToken();
      const headers: { [key: string]: string } = {};
      if (jwtToken) {
        headers['Authorization'] = `Bearer ${jwtToken}`;
      }
      const mimicAddr = this.stateService.getMimicAddress();
      if (mimicAddr) {
        headers['X-Mimic-As'] = mimicAddr;
      }

      // Fetch bids from API (will return confidential bids if authenticated)
      const response = await firstValueFrom(
        this.http.get<any>(url, { headers: Object.keys(headers).length > 0 ? headers : undefined })
      );
      
      // Handle different response structures
      let bidsArray: PunkBid[] = [];
      
      if (response) {
        // Check if response is already an array
        if (Array.isArray(response)) {
          bidsArray = response;
        } 
        // Check if response has a data property with an array
        else if (response.data && Array.isArray(response.data)) {
          bidsArray = response.data;
        }
        // Check if response has a bids property with an array
        else if (response.bids && Array.isArray(response.bids)) {
          bidsArray = response.bids;
        }
        // Log the response structure for debugging
        else {
          console.warn('Unexpected response structure:', response);
          bidsArray = [];
        }
      }
      
      // Process bids to add decodedTags, bidType, and bidTypeLabel
      const processedBids = bidsArray.map((bid) => {
        const decodedTags = this.bidTagsService.decodeCollectionBidTags(bid);
        const bidType = this.bidTagsService.getCollectionBidType(bid, decodedTags);
        
        // Extract owner address for owner bids
        let ownerAddress: string | undefined;
        if (bidType === 'owner' && decodedTags.length > 0) {
          const ownerTag = decodedTags[0];
          const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
          if (match) {
            ownerAddress = match[1].toLowerCase();
          }
        }
        
        // Extract punk indices - handle both punk_indices and selected_punk_ids
        let punkIndices: number[] = [];
        if (bid.punk_indices) {
          try {
            punkIndices = JSON.parse(bid.punk_indices);
          } catch (e) {
            console.warn('Failed to parse punk_indices:', bid.punk_indices, e);
            punkIndices = [];
          }
        } else if (bid.selected_punk_ids) {
          // For targeted bids, use selected_punk_ids if punk_indices is not available
          try {
            if (typeof bid.selected_punk_ids === 'string') {
              punkIndices = JSON.parse(bid.selected_punk_ids);
            } else if (Array.isArray(bid.selected_punk_ids)) {
              punkIndices = bid.selected_punk_ids;
            }
          } catch (e) {
            console.warn('Failed to parse selected_punk_ids:', bid.selected_punk_ids, e);
            punkIndices = [];
          }
        }
        
        const punkIndicesCount = bid.punk_indices_count != null ? Number(bid.punk_indices_count) : punkIndices.length;
        return {
          ...bid,
          decodedTags,
          bidType,
          punkIndices,
          punk_indices_count: punkIndicesCount,
          bidTypeLabel: bidType === 'owner' ? 'Owner Bid' : bidType === 'custom' ? 'Custom Bid' : bidType === 'trait' ? 'Trait Bid' : 'Collection Bid',
          ownerAddress,
          isConfidential: !!(bid.confidential === true || (typeof bid.confidential === 'string' && bid.confidential === 'true') || (typeof bid.confidential === 'number' && bid.confidential === 1)),
          stash_funded: normalizeStashFundedForUi(bid.stash_funded),
          // Precompute formatted values to avoid method calls in template
          formattedExpiresAt: bid.expires_at ? this.formatDate(bid.expires_at) : null,
          formattedPlatform: bid.source ? this.formatPlatform(bid.source) : null,
        };
      });
      
      const stashOutgoingBids = processedBids.filter((bid) =>
        this.shouldShowInOutgoingList(bid),
      );

      // Group duplicate bids (same bidder_address + bid_nonce from different sources)
      const groupedBids = this.groupDuplicateBids(stashOutgoingBids);

      let onChainBids: PunkBid[] = [];
      try {
        onChainBids = await this.fetchOutgoingPunksmarketBids(wallets);
      } catch (onChainErr) {
        console.warn('[MyBids] Outgoing on-chain bids failed; showing stash bids only.', onChainErr);
      }

      // Sort bids by amount (descending) then by date (descending)
      const sortedBids = this.sortBids([...groupedBids, ...onChainBids]);

      // Outgoing bids: always show all bids (no "higher bid per token" filter)
      // Precompute delegated status and tooltip options for each bid
      this.bids = sortedBids.map(bid => {
        const isDelegated = this.isDelegatedBid(bid);
        return {
          ...bid,
          isDelegated,
          cancelButtonTooltipOptions: isDelegated ? {
            content: 'This is a bid from a delegated wallet. Only the bidder can cancel this bid.',
            placement: 'top' as const
          } : undefined
        };
      });

      // Update computed properties
      this.updateComputedProperties();
    } catch (error) {
      console.error('Error loading bids:', error);
      this.error = 'Failed to load bids. Please try again later.';
      this.bids = [];
    } finally {
      this.loading = false;
    }
  }

  private getActiveIdentityAddress(
    walletAddressOverride?: string | null,
    authenticatedAsOverride?: string | null
  ): string | null {
    const walletAddress = walletAddressOverride ?? this.walletAddress;
    if (!walletAddress) {
      return null;
    }

    // Mimic mode always uses the effective wallet emitted by walletAddress$.
    if (this.stateService.isMimicking()) {
      return walletAddress.toLowerCase();
    }

    const authenticatedAsAddress =
      authenticatedAsOverride !== undefined
        ? authenticatedAsOverride
        : this.stateService.getEffectiveAuthenticatedAsAddress();

    return (authenticatedAsAddress || walletAddress).toLowerCase();
  }

  private getAllWalletAddresses(): string[] {
    const addresses = new Set<string>();

    // Use the currently active identity in UI:
    // mimic address (if active), otherwise selected delegated wallet, otherwise connected wallet.
    const activeWalletAddress = this.getActiveIdentityAddress();
    if (activeWalletAddress) {
      addresses.add(activeWalletAddress);
    }

    return Array.from(addresses);
  }

  /**
   * Groups duplicate bids that have the same bidder_address and bid_nonce
   * but come from different sources (e.g., cryptopunks.eth.limo and cryptopunks.app)
   * @param bids Array of bids to group
   * @returns Array of grouped bids with sources array
   */
  private groupDuplicateBids(bids: PunkBid[]): PunkBid[] {
    if (!Array.isArray(bids) || bids.length === 0) {
      return bids;
    }

    // Create a map to group bids by bidder_address + bid_nonce
    const bidMap = new Map<string, PunkBid[]>();

    for (const bid of bids) {
      // Create a unique key from bidder_address (lowercase) and bid_nonce
      const key = `${(bid.bidder_address || '').toLowerCase()}_${bid.bid_nonce}`;
      
      if (!bidMap.has(key)) {
        bidMap.set(key, []);
      }
      bidMap.get(key)!.push(bid);
    }

    // Process each group
    const groupedBids: PunkBid[] = [];

    for (const [key, bidGroup] of bidMap.entries()) {
      if (bidGroup.length === 1) {
        // Single bid, just ensure it has sources array
        const bid = bidGroup[0];
        groupedBids.push({
          ...bid,
          sources: bid.source ? [bid.source] : [],
        });
      } else {
        // Multiple bids with same bidder_address + bid_nonce
        // Prefer cryptopunks.eth.limo as base (for accept/cancel to use that id when available)
        const baseBid = bidGroup.find((b) => b.source === 'cryptopunks.eth.limo') || bidGroup[0];
        const sources = new Set<string>();
        const additionalBidIds = new Set<string>();
        
        // Collect all unique sources and other bid ids
        for (const bid of bidGroup) {
          if (bid.source) {
            sources.add(bid.source);
          }
          if (bid.id && bid.id !== baseBid.id) {
            additionalBidIds.add(bid.id);
          }
        }

        // Sort sources: cryptopunks.eth.limo should always be first
        const sourcesArray = Array.from(sources);
        sourcesArray.sort((a, b) => {
          if (a === 'cryptopunks.eth.limo') return -1;
          if (b === 'cryptopunks.eth.limo') return 1;
          return a.localeCompare(b);
        });

        groupedBids.push({
          ...baseBid,
          sources: sourcesArray,
          additionalBidIds: Array.from(additionalBidIds),
          stash_funded: bidGroup.some((b) => b.stash_funded === 0) ? 0 : 1,
        });
      }
    }

    return groupedBids;
  }

  private sortBids(bids: PunkBid[]): PunkBid[] {
    // Ensure bids is an array
    if (!Array.isArray(bids)) {
      console.warn('sortBids received non-array:', bids);
      return [];
    }
    
    return [...bids].sort((a, b) => {
      // First sort by amount (descending)
      const amountDiff = (b.bid_amount_eth || 0) - (a.bid_amount_eth || 0);
      if (amountDiff !== 0) {
        return amountDiff;
      }
      
      // Then sort by date (descending - most recent first)
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });
  }

  cancelBid(bid: PunkBid): void {
    this.selectedBidForCancellation = bid;
    this.cancelBidConfirmationModalActive = true;
  }

  closeCancelBidConfirmation(): void {
    this.cancelBidConfirmationModalActive = false;
    this.selectedBidForCancellation = null;
  }

  async confirmCancelBid(): Promise<void> {
    if (!this.selectedBidForCancellation || !this.walletAddress) {
      return;
    }

    const bid = this.selectedBidForCancellation;
    if (bid.punksmarketBidId) {
      await this.confirmCancelOnChainBid(bid);
      return;
    }

    try {
      // Get the user's stash address
      const stashAddress = await this.web3Service.getStashAddressFor(this.walletAddress);
      
      // Close confirmation modal
      this.closeCancelBidConfirmation();
      
      // Mark that this transaction is from my-bids
      this.isMyBidsTransactionActive = true;
      
      // Initialize transaction tracking
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the cancel bid transaction in your wallet.</p>');
      
      // Call cancelBid on the stash contract
      const hash = await this.web3Service.cancelBidOnStash(stashAddress, bid.bid_nonce);
      
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }
      
      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your cancel bid transaction is being processed on the Ethereum network.</p>');
      
      // Wait for transaction receipt
      const receipt = await this.pollReceipt(hash);
      const txHash = receipt.transactionHash;
      const shortHash = `${txHash.slice(0, 6)}...${txHash.slice(-6)}`;
      const etherscanUrl = this.getEtherscanTxUrl(txHash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, `<p>Your bid has been canceled successfully!</p><p>Transaction hash: <a href="${etherscanUrl}" target="_blank">${shortHash}</a></p><p>View it <a href="${etherscanUrl}" target="_blank">here</a></p>`);
      
      // Invalidate or validate the cancellation on the server for all sources
      try {
        // Extract base URL from alternativeListingsGraphURI
        const graphUri = environment.alternativeListingsGraphURI || '';
        const baseUrl = graphUri.replace(/\/graphql$/, '');
        
        if (baseUrl) {
          // Get all sources (either from sources array or fallback to source)
          const sources = bid.sources && bid.sources.length > 0 
            ? bid.sources 
            : (bid.source ? [bid.source] : []);
          
          // Invalidate/validate for each source
          for (const source of sources) {
            try {
              if (source === 'cryptopunks.app') {
                // If source is cryptopunks.app, invalidate the bid directly in database
                const invalidateUrl = `${baseUrl}/api/scraper/punk-bids/invalidate/${bid.bid_nonce}`;
                await firstValueFrom(this.http.put(invalidateUrl, {}));
              } else {
                // For other sources, use the validation endpoint
                const validateUrl = `${baseUrl}/api/scraper/punk-bids/validate/${bid.bid_nonce}`;
                await firstValueFrom(this.http.put(validateUrl, {}));
              }
            } catch (sourceError) {
              console.error(`Error invalidating/validating bid cancellation for source ${source}:`, sourceError);
              // Continue with other sources even if one fails
            }
          }
        }
      } catch (validateError) {
        console.error('Error invalidating/validating bid cancellation:', validateError);
        // Continue even if validation fails
      }
      
      // Wait 0.5 seconds before reloading bids
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload bids to reflect the cancellation
      await this.loadBids();
      
      // Modal will remain open until user closes it manually
    } catch (err) {
      console.error('Error canceling bid:', err);
      this.txTrackingSvc.setErrorTransactionMessage(err, '<p>There was an error canceling your bid.</p>');
      // Modal will remain open until user closes it manually
    }
  }

  async pollReceipt(hash: string): Promise<TransactionReceipt> {
    let resolved = false;
    return new Promise(async (resolve, reject) => {
      while (!resolved) {
        console.log('polling receipt', hash);
        try {
          const receipt = await this.web3Service.waitForTransaction(hash);
          if (receipt) {
            resolved = true;
            resolve(receipt);
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  }

  cancelAllBids(): void {
    if (!this.hasStashOutgoingBids) {
      return;
    }
    this.cancelAllBidsConfirmationModalActive = true;
  }

  closeCancelAllBidsConfirmation(): void {
    this.cancelAllBidsConfirmationModalActive = false;
  }

  async confirmCancelAllBids(): Promise<void> {
    if (!this.walletAddress || !this.hasStashOutgoingBids) {
      return;
    }

    try {
      const totalBids = this.bids.filter((bid) => !bid.punksmarketBidId).length;
      
      // Close confirmation modal
      this.closeCancelAllBidsConfirmation();
      
      // Mark that this transaction is from my-bids
      this.isMyBidsTransactionActive = true;
      
      // Get the user's stash address
      const stashAddress = await this.web3Service.getStashAddressFor(this.walletAddress);
      
      // Initialize transaction tracking
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the cancel all bids transaction in your wallet.</p>');
      
      // Call cancelAllPunkBids on the stash contract
      const hash = await this.web3Service.cancelAllPunkBidsOnStash(stashAddress);
      
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }
      
      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your cancel all bids transaction is being processed on the Ethereum network.</p>');
      
      // Wait for transaction receipt
      const receipt = await this.pollReceipt(hash);
      const txHash = receipt.transactionHash;
      const shortHash = `${txHash.slice(0, 6)}...${txHash.slice(-6)}`;
      const etherscanUrl = this.getEtherscanTxUrl(txHash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, `<p>All ${totalBids} bid(s) have been canceled successfully!</p><p>Transaction hash: <a href="${etherscanUrl}" target="_blank">${shortHash}</a></p><p>View it <a href="${etherscanUrl}" target="_blank">here</a></p>`);
      
      // Invalidate confidential rows immediately; then run full bidder validation for other sources
      try {
        await this.web3Service.notifyIndexerInvalidateAllConfidentialBidsAfterCancelAll();
      } catch (indexerInvalidateAllError) {
        console.error('Error invalidating pending bids on indexer:', indexerInvalidateAllError);
      }
      try {
        const graphUri = environment.alternativeListingsGraphURI || '';
        const baseUrl = graphUri.replace(/\/graphql$/, '');
        
        if (baseUrl && this.walletAddress) {
          const validateUrl = `${baseUrl}/api/scraper/punk-bids/validate/bidder/${this.walletAddress}`;
          await firstValueFrom(this.http.put(validateUrl, {}));
        }
      } catch (validateError) {
        console.error('Error validating bid cancellations:', validateError);
        // Continue even if validation fails
      }
      
      // Wait 0.5 seconds before reloading bids
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload bids to reflect the cancellations
      await this.loadBids();
      
      // Modal will remain open until user closes it manually
    } catch (err) {
      console.error('Error canceling all bids:', err);
      this.txTrackingSvc.setErrorTransactionMessage(err, '<p>There was an error canceling your bids.</p>');
      // Modal will remain open until user closes it manually
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatPlatform(source?: string): string {
    if (!source) return 'Unknown';
    // Capitalize first letter and format platform name
    return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase();
  }

  trackByBidId(index: number, bid: PunkBid | any): string {
    return bid.id || index.toString();
  }

  trackByPunkId(index: number, punkId: number): number {
    return punkId;
  }

  async loadStashInfo(): Promise<void> {
    if (!this.walletAddress) return;

    this.loadingStash = true;

    try {
      // Get stash address
      const stashAddress = await this.web3Service.getStashAddressFor(this.walletAddress);
      this.stashAddress = stashAddress;

      // Get stash ETH balance using availableLiquidity with zero address (ETH)
      // This uses the contract's availableLiquidity function with 0x0000...0000 for ETH
      const balanceWei = await this.web3Service.getStashAvailableEth(stashAddress);
      this.stashBalanceWei = balanceWei;
      // Format directly from wei to avoid precision loss from parseFloat
      // Keep as number for template compatibility but calculate from wei when needed
      const ethString = formatEther(balanceWei);
      this.stashBalanceEth = parseFloat(ethString);
    } catch (error) {
      console.error('Error loading stash info:', error);
      // Don't show error to user, just log it
      this.stashAddress = null;
      this.stashBalanceWei = null;
      this.stashBalanceEth = 0;
    } finally {
      this.loadingStash = false;
      // Update computed properties after stash info changes
      this.updateComputedProperties();
    }
  }

  /**
   * Update all computed properties that are used in the template
   * This avoids calling methods in the template which would be called on every change detection cycle
   */
  private updateComputedProperties(): void {
    this.hasStashOutgoingBids = this.bids.some((bid) => !bid.punksmarketBidId);

    // Formatted stash balance
    this.formattedStashBalance = this.getFormattedStashBalance();
    
    // Stash etherscan URL
    if (this.stashAddress) {
      this.stashEtherscanUrl = this.getEtherscanUrl(this.stashAddress);
    } else {
      this.stashEtherscanUrl = '';
    }
    
    // Can withdraw flag - check if withdraw amount is valid and not exceeding balance
    if (!this.stashBalanceWei || this.stashBalanceWei === 0n || this.withdrawing) {
      this.canWithdrawFlag = false;
    } else {
      const withdrawAmountStr = String(this.withdrawAmount || '').trim();
      if (!withdrawAmountStr) {
        this.canWithdrawFlag = false;
      } else {
        const withdrawAmountNum = parseFloat(withdrawAmountStr);
        if (isNaN(withdrawAmountNum) || withdrawAmountNum <= 0) {
          this.canWithdrawFlag = false;
        } else {
          try {
            const withdrawAmountWei = parseEther(withdrawAmountStr);
            // Check if the parsed amount is valid and doesn't exceed balance
            this.canWithdrawFlag = withdrawAmountWei > 0n && withdrawAmountWei <= this.stashBalanceWei;
          } catch (e) {
            // Invalid amount format (e.g., too many decimal places, invalid characters)
            this.canWithdrawFlag = false;
          }
        }
      }
    }
    
    // Can deposit flag
    this.canDepositFlag = !this.depositing && !!this.depositAmount && !isNaN(parseFloat(this.depositAmount)) && parseFloat(this.depositAmount) > 0;
  }

  /**
   * Get formatted stash balance for display
   * Formats directly from stashBalanceWei to avoid precision loss
   * This ensures the displayed value matches the actual withdrawable amount
   */
  getFormattedStashBalance(): string {
    if (!this.stashBalanceWei || this.stashBalanceWei === 0n) {
      return '0';
    }
    // Format directly from wei to preserve precision
    // formatEther returns a precise string representation
    const ethString = formatEther(this.stashBalanceWei);
    // Convert to number for formatting, but this is only for display
    // The actual withdraw uses stashBalanceWei (bigint) which is precise
    const numValue = Number(ethString);
    // Format to 4 decimal places for display
    return numValue.toFixed(4);
  }

  withdrawFromStash(): void {
    if (!this.stashAddress || !this.stashBalanceWei || this.stashBalanceWei === 0n) {
      return;
    }
    
    // Open withdraw modal and set default amount to max
    this.withdrawModalActive = true;
    this.setMaxWithdrawAmount();
  }

  closeWithdrawModal(): void {
    this.withdrawModalActive = false;
    this.withdrawAmount = '';
    this.updateComputedProperties();
  }

  setMaxWithdrawAmount(): void {
    if (this.stashBalanceWei && this.stashBalanceWei > 0n) {
      // Format directly from wei to preserve precision
      // Use formatEther which returns a precise string representation
      const ethString = formatEther(this.stashBalanceWei);
      // Store the precise string value (formatEther already handles precision correctly)
      this.withdrawAmount = ethString;
      // Force update after setting the value programmatically
      // ngModelChange might not fire when value is set programmatically
      setTimeout(() => {
        this.updateComputedProperties();
      }, 0);
    }
  }

  async confirmWithdraw(): Promise<void> {
    if (!this.stashAddress || !this.stashBalanceWei || this.stashBalanceWei === 0n || this.withdrawing) {
      return;
    }

    const amountString = String(this.withdrawAmount).trim();
    if (!amountString || isNaN(parseFloat(amountString)) || parseFloat(amountString) <= 0) {
      return;
    }

    // Convert ETH to wei and validate it doesn't exceed balance
    const amountWeiBigInt = parseEther(amountString);
    if (amountWeiBigInt > this.stashBalanceWei) {
      return;
    }

    try {
      this.withdrawing = true;
      this.closeWithdrawModal();
      this.isMyBidsTransactionActive = true;

      // Initialize transaction tracking
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the withdraw transaction in your wallet.</p>');

      // Call withdraw on the stash contract with the specified amount
      const hash = await this.web3Service.withdrawEthFromStash(this.stashAddress, amountWeiBigInt);

      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your withdraw transaction is being processed on the Ethereum network.</p>');

      // Wait for transaction receipt
      const receipt = await this.pollReceipt(hash);
      const txHash = receipt.transactionHash;
      const shortHash = `${txHash.slice(0, 6)}...${txHash.slice(-6)}`;
      const etherscanUrl = this.getEtherscanTxUrl(txHash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, `<p>Your funds have been withdrawn successfully!</p><p>Transaction hash: <a href="${etherscanUrl}" target="_blank">${shortHash}</a></p><p>View it <a href="${etherscanUrl}" target="_blank">here</a></p>`);

      // Wait 0.5 seconds before reloading stash info
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload stash info to reflect the withdrawal
      await this.loadStashInfo();

      // Reset withdrawing flag, but keep modal open for user to close manually
      this.withdrawing = false;
      // Update computed properties
      this.updateComputedProperties();
      // Modal will remain open until user closes it manually
    } catch (err) {
      console.error('Error withdrawing from stash:', err);
      this.txTrackingSvc.setErrorTransactionMessage(err, '<p>There was an error withdrawing your funds.</p>');
      // Reset withdrawing flag, but keep modal open for user to close manually
      this.withdrawing = false;
      // Update computed properties
      this.updateComputedProperties();
      // Modal will remain open until user closes it manually
    }
  }

  /**
   * Update canWithdrawFlag when withdrawAmount changes
   * This should be called from the template via (ngModelChange) or similar
   */
  onWithdrawAmountChange(): void {
    this.updateComputedProperties();
  }

  getEtherscanUrl(address: string): string {
    // Sepolia chainId is 11155111, mainnet is 1
    if (environment.chainId === 11155111) {
      return `https://sepolia.etherscan.io/address/${address}`;
    }
    return `https://etherscan.io/address/${address}`;
  }

  getEtherscanTxUrl(txHash: string): string {
    // Sepolia chainId is 11155111, mainnet is 1
    if (environment.chainId === 11155111) {
      return `https://sepolia.etherscan.io/tx/${txHash}`;
    }
    return `https://etherscan.io/tx/${txHash}`;
  }

  canWithdraw(): boolean {
    return !this.withdrawing && this.stashBalanceWei !== null && this.stashBalanceWei > 0n;
  }

  depositToStash(): void {
    this.depositModalActive = true;
    this.depositAmount = '';
  }

  closeDepositModal(): void {
    this.depositModalActive = false;
    this.depositAmount = '';
  }

  async confirmDeposit(): Promise<void> {
    if (!this.stashAddress || !this.walletAddress) {
      return;
    }

    const amountString = String(this.depositAmount).trim();
    if (!amountString || isNaN(parseFloat(amountString)) || parseFloat(amountString) <= 0) {
      return;
    }

    try {
      this.depositing = true;
      this.closeDepositModal();
      this.isMyBidsTransactionActive = true;

      // Convert ETH to wei
      const amountWeiBigInt = parseEther(amountString);
      const amountWeiString = amountWeiBigInt.toString();

      // Initialize transaction tracking
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the deposit transaction in your wallet.</p>');

      // Send ETH to stash
      const hash = await this.web3Service.sendEthToAddress(this.stashAddress, amountWeiString);

      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your deposit transaction is being processed on the Ethereum network.</p>');

      // Wait for transaction receipt
      const receipt = await this.pollReceipt(hash);
      const txHash = receipt.transactionHash;
      const shortHash = `${txHash.slice(0, 6)}...${txHash.slice(-6)}`;
      const etherscanUrl = this.getEtherscanTxUrl(txHash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, `<p>Your funds have been deposited successfully!</p><p>Transaction hash: <a href="${etherscanUrl}" target="_blank">${shortHash}</a></p><p>View it <a href="${etherscanUrl}" target="_blank">here</a></p>`);

      // Wait 0.5 seconds before reloading stash info
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload stash info to reflect the deposit
      await this.loadStashInfo();

      // Reset depositing flag, but keep modal open for user to close manually
      this.depositing = false;
      // Update computed properties
      this.updateComputedProperties();
      // Modal will remain open until user closes it manually
    } catch (err) {
      console.error('Error depositing to stash:', err);
      this.txTrackingSvc.setErrorTransactionMessage(err, '<p>There was an error depositing your funds.</p>');
      // Reset depositing flag, but keep modal open for user to close manually
      this.depositing = false;
      // Update computed properties
      this.updateComputedProperties();
      // Modal will remain open until user closes it manually
    }
  }

  /**
   * Update canDepositFlag when depositAmount changes
   * This should be called from the template via (ngModelChange) or similar
   */
  onDepositAmountChange(): void {
    this.updateComputedProperties();
  }

  // Keep canDeposit() method for backward compatibility but prefer using canDepositFlag in template
  canDeposit(): boolean {
    return !this.depositing && !!this.depositAmount && !isNaN(parseFloat(this.depositAmount)) && parseFloat(this.depositAmount) > 0;
  }

  getBidIdTooltipContent(bidId: string, additionalBidIds: string[] = [], bid?: unknown): string {
    const allBidIds = [bidId, ...additionalBidIds.filter((id) => !!id && id !== bidId)];
    let copyPayloadBase64 = '';
    try {
      const textToCopy = bid != null
        ? JSON.stringify(bid, null, 2)
        : bidId;
      copyPayloadBase64 = btoa(unescape(encodeURIComponent(textToCopy)));
    } catch {
      copyPayloadBase64 = btoa(unescape(encodeURIComponent(bidId)));
    }
    const bidIdsHtml = allBidIds
      .map((id, index) => `
        <div class="bid-id-label" style="font-size: 10px; color: rgba(255, 255, 255, 0.6); margin-bottom: 0.2rem;">${index === 0 ? 'Bid ID:' : `Additional ID ${index}:`}</div>
        <div class="bid-id-value" style="font-size: 11px; color: rgba(255, 255, 255, 0.9); font-family: monospace; margin-bottom: 0.6rem; word-break: break-all;">${id}</div>
      `)
      .join('');

    const copyOnClick = `(function(e){ var p=e.target.getAttribute('data-copy-payload'); var t=p?decodeURIComponent(unescape(atob(p))):''; if(t)navigator.clipboard.writeText(t).then(function(){ e.target.textContent='Copied!'; setTimeout(function(){ e.target.textContent='Copy'; }, 2000); }); })(event)`;
    return `
      <div class="bid-id-tooltip" style="padding: 0.4rem;">
        ${bidIdsHtml}
        <button class="copy-bid-id-btn" data-copy-payload="${copyPayloadBase64}" onclick="${copyOnClick}" style="font-size: 10px; padding: 0.3rem 0.6rem; background: rgba(var(--pink), 1); color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 600; width: 100%;">Copy</button>
      </div>
    `;
  }

  /**
   * Check if a bid is from a delegated wallet (not the connected wallet)
   * @param bid The bid to check
   * @returns true if the bid is from a delegated wallet, false otherwise
   */
  isDelegatedBid(bid: PunkBid): boolean {
    if (!this.walletAddress || !bid.bidder_address) {
      return false;
    }
    // Compare addresses in lowercase to ensure case-insensitive comparison
    return bid.bidder_address.toLowerCase() !== this.walletAddress.toLowerCase();
  }

  canCancelOutgoingBid(bid: PunkBid): boolean {
    if (bid.punksmarketBidId) {
      return environment.featureFlags?.PUNKSMARKET_BID_NATIVE !== false;
    }
    return true;
  }

  isOnChainOutgoingBid(bid: PunkBid): boolean {
    return !!bid.punksmarketBidId;
  }

  /**
   * Min-ETH hide applies to broad collection bids only (same idea as punk profile on-chain table).
   * Targeted / on-chain outgoing bids stay visible under the default 1 ETH threshold.
   */
  private shouldShowInOutgoingList(bid: PunkBid): boolean {
    if (bid.punksmarketBidId) {
      return true;
    }
    if (!this.displayOnlyBidsAboveMinEth) {
      return true;
    }
    const amount = Number(bid.bid_amount_eth) || 0;
    if (amount >= this.minBidEthConfig) {
      return true;
    }
    const targetedCount =
      bid.punk_indices_count ??
      bid.punkIndices?.length ??
      (bid.selected_punk_ids
        ? Array.isArray(bid.selected_punk_ids)
          ? bid.selected_punk_ids.length
          : 0
        : 0);
    if (bid.bidType === 'custom' || bid.bidType === 'owner' || targetedCount > 0) {
      return true;
    }
    return false;
  }

  private punksmarketBidsEnabled(): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
    );
  }

  private async fetchOutgoingPunksmarketBids(wallets: string[]): Promise<PunkBid[]> {
    if (!this.punksmarketBidsEnabled()) {
      return [];
    }

    const results = await Promise.all(
      wallets.map(async (wallet) => {
        try {
          const res = await firstValueFrom(
            this.dataSvc.fetchPunksmarketBids({
              active: true,
              bidder: wallet,
              limit: 100,
              sort: 'bid_wei-desc',
            }),
          );
          return (res.items ?? []).map((item) =>
            this.mapPunksmarketToOutgoingBid(
              unifiedBidToStashTableShape(mapPunksmarketIndexerBidToUnified(item)),
            ),
          );
        } catch (err) {
          console.warn('[MyBids] Failed to load on-chain bids for', wallet, err);
          return [] as PunkBid[];
        }
      }),
    );

    const byId = new Map<string, PunkBid>();
    for (const bid of results.flat()) {
      if (this.shouldShowInOutgoingList(bid)) {
        byId.set(bid.id, bid);
      }
    }
    return [...byId.values()];
  }

  private fetchIncomingPunksmarketBids$(ownedPunkIds: number[]): Observable<IncomingBid[]> {
    if (!this.punksmarketBidsEnabled() || ownedPunkIds.length === 0) {
      return of([]);
    }

    const uniqueIds = [...new Set(ownedPunkIds)];
    const concurrency = 10;

    return from(uniqueIds).pipe(
      mergeMap(
        (punkId) =>
          this.dataSvc.fetchPunksmarketBidsForPunk(punkId).pipe(
            map((items) => ({ punkId, items: items ?? [] })),
            catchError((err) => {
              console.warn('[MyBids] Incoming on-chain bids for punk', punkId, err);
              return of({ punkId, items: [] as PunksmarketIndexerBid[] });
            }),
          ),
        concurrency,
      ),
      toArray(),
      map((results) => this.mergeIncomingPunksmarketResults(results)),
    );
  }

  private mergeIncomingPunksmarketResults(
    results: { punkId: number; items: PunksmarketIndexerBid[] }[],
  ): IncomingBid[] {
    const activeIdentity = this.getActiveIdentityAddress();
    const byPmId = new Map<
      string,
      IncomingBid & { _matching: Set<number> }
    >();

    for (const { punkId, items } of results) {
      for (const item of items) {
        if (item.active === false) {
          continue;
        }
        if (
          activeIdentity &&
          item.bidder?.toLowerCase() === activeIdentity.toLowerCase()
        ) {
          continue;
        }

        const key = item.bidId;
        let row = byPmId.get(key);
        if (!row) {
          const shape = unifiedBidToStashTableShape(
            mapPunksmarketIndexerBidToUnified(item),
          );
          const bid = this.mapPunksmarketToOutgoingBid(shape) as IncomingBid;
          row = { ...bid, _matching: new Set([punkId]) };
          byPmId.set(key, row);
        } else {
          row._matching.add(punkId);
        }
      }
    }

    const merged: IncomingBid[] = [];
    for (const row of byPmId.values()) {
      const matching = [...row._matching].sort((a, b) => a - b);
      const { _matching: _ignored, ...bid } = row;
      const incoming: IncomingBid = {
        ...bid,
        matchingOwnedPunks: matching,
        punksForFrise: matching,
        punkIndices: matching.slice(0, 3),
        punk_indices_count: matching.length,
      };
      if (this.shouldShowInOutgoingList(incoming as PunkBid)) {
        merged.push(incoming);
      }
    }
    return merged;
  }

  private mapPunksmarketToOutgoingBid(
    row: ReturnType<typeof unifiedBidToStashTableShape>,
  ): PunkBid {
    const includeIds = this.normalizePunkIdList(
      row.punkIds ?? (row as { punkIndices?: unknown }).punkIndices,
    );
    const criteriaTags = Array.isArray(row.decodedTags)
      ? row.decodedTags.filter((tag): tag is string => typeof tag === 'string')
      : [];
    const punksmarketBidId =
      typeof row.punksmarketBidId === 'string'
        ? row.punksmarketBidId
        : undefined;
    const hasTargetPunks = includeIds.length > 0;
    const classified =
      row.punksmarketIndexerBid != null
        ? classifyPunksmarketIndexerBid(row.punksmarketIndexerBid)
        : null;
    return {
      id: row.id,
      bidder_address: row.bidder_address,
      bid_amount_wei: String(row.bid_amount_wei ?? '0'),
      bid_amount_eth: Number(row.bid_amount_eth) || 0,
      bid_nonce: 0,
      account_nonce: 0,
      merkle_root: '',
      signature: '',
      status: row.status ?? 'active',
      created_at: row.created_at ?? new Date().toISOString(),
      bidType:
        classified?.bidType ??
        (hasTargetPunks ? 'custom' : (row.bidType as PunkBid['bidType']) ?? 'collection'),
      bidTypeLabel:
        classified?.bidTypeLabel ??
        (typeof row.bidTypeLabel === 'string' ? row.bidTypeLabel : onChainBidTypeLabel),
      punkIndices: includeIds.slice(0, 3),
      punk_indices_count: includeIds.length,
      decodedTags: classified?.display.tags ?? (hasTargetPunks ? [] : criteriaTags),
      source: 'punksmarket.app',
      platform: 'punksmarket',
      punksmarketBidId,
      stash_funded: 1,
      isConfidential: false,
      formattedExpiresAt: null,
      formattedPlatform: 'punksmarket.app',
    };
  }

  private normalizePunkIdList(value: unknown): number[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id): id is number => Number.isInteger(id));
  }

  private async confirmCancelOnChainBid(bid: PunkBid): Promise<void> {
    if (!bid.punksmarketBidId) {
      return;
    }

    try {
      this.closeCancelBidConfirmation();
      this.isMyBidsTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage(
        '<p>Please confirm the on-chain bid cancellation in your wallet.</p>',
      );

      const hash = await this.punksmarketV1Svc.cancelBid(bid.punksmarketBidId);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(
        hash,
        '<p>Your on-chain bid cancellation is being processed on the Ethereum network.</p>',
      );

      const receipt = await this.pollReceipt(hash);
      const txHash = receipt.transactionHash;
      const shortHash = `${txHash.slice(0, 6)}...${txHash.slice(-6)}`;
      const etherscanUrl = this.getEtherscanTxUrl(txHash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        `<p>${onChainBidCancelSuccess}</p><p>Transaction hash: <a href="${etherscanUrl}" target="_blank">${shortHash}</a></p><p>View it <a href="${etherscanUrl}" target="_blank">here</a></p>`,
      );

      await new Promise((resolve) => setTimeout(resolve, 500));
      await this.loadBids();
    } catch (err) {
      console.error('Error canceling on-chain bid:', err);
      this.txTrackingSvc.setErrorTransactionMessage(
        err,
        `<p>${onChainBidCancelFailure}</p>`,
      );
    }
  }

  /**
   * Calculate which owned punks match a given bid
   * @param bid The bid to check
   * @param bidType The type of the bid (collection, custom, trait, owner)
   * @returns Array of owned punk IDs that match this bid
   */
  private calculateMatchingOwnedPunks(bid: any, bidType: 'trait' | 'collection' | 'custom' | 'owner'): number[] {
    if (this.ownedPunkIds.length === 0) {
      return [];
    }

    // For collection bids (punk_indices = [] or empty), all owned punks match
    if (bidType === 'collection') {
      return [...this.ownedPunkIds];
    }

    // For owner bids, all owned punks match (bid is on the owner)
    if (bidType === 'owner') {
      return [...this.ownedPunkIds];
    }

    // For custom/targeted bids, check punk_indices
    if (bidType === 'custom') {
      let bidPunkIndices: number[] = [];
      try {
        // Try to extract punk indices from various formats
        if (bid.punk_indices) {
          if (typeof bid.punk_indices === 'string') {
            bidPunkIndices = JSON.parse(bid.punk_indices);
          } else if (Array.isArray(bid.punk_indices)) {
            bidPunkIndices = bid.punk_indices;
          }
        } else if (bid.selected_punk_ids) {
          if (typeof bid.selected_punk_ids === 'string') {
            bidPunkIndices = JSON.parse(bid.selected_punk_ids);
          } else if (Array.isArray(bid.selected_punk_ids)) {
            bidPunkIndices = bid.selected_punk_ids;
          }
        } else if (bid.punkIndices) {
          if (Array.isArray(bid.punkIndices)) {
            bidPunkIndices = bid.punkIndices;
          }
        }
      } catch (error) {
        console.warn('Error parsing punk indices for matching:', error);
        return [];
      }

      // Filter to only owned punks that are in the bid's punk_indices
      if (bidPunkIndices.length > 0) {
        return this.ownedPunkIds.filter(id => bidPunkIndices.includes(id));
      }

      // If no punk_indices found, return empty (shouldn't happen for custom bids)
      return [];
    }

    // For trait bids, the backend already filtered to bids that match at least one owned punk
    // Since we can't easily check traits here, we'll return all owned punks
    // In a more sophisticated implementation, we could check traits
    // For now, return all owned punks as the backend already filtered
    return [...this.ownedPunkIds];
  }

  canAcceptStashBid = (bid: any): boolean => {
    if (!this.isConnected || !this.walletAddress) {
      return false;
    }

    if ((bid as any).isLegacyBid) {
      if (bid.punk_id !== undefined) {
        return this.ownedPunkIds.includes(bid.punk_id);
      }
      return bid.matchingOwnedPunks && bid.matchingOwnedPunks.length > 0;
    }

    const activeWallet = this.getActiveIdentityAddress();
    if (activeWallet && bid.bidder_address) {
      if (bid.bidder_address.toLowerCase() === activeWallet.toLowerCase()) {
        return false;
      }
    }

    if (bid.punksmarketBidId || bid.platform === 'punksmarket') {
      if (
        !this.punksmarketBidsEnabled() ||
        environment.featureFlags?.PUNKSMARKET_BID_NATIVE === false
      ) {
        return false;
      }
      const matching: number[] = bid.matchingOwnedPunks ?? [];
      return matching.length > 0;
    }
    
    /*
    // Check if bid targets any owned punks
    try {
      let bidPunkIndices: number[] = [];
      if (bid.punk_indices) {
        if (typeof bid.punk_indices === 'string') {
          bidPunkIndices = JSON.parse(bid.punk_indices);
        } else if (Array.isArray(bid.punk_indices)) {
          bidPunkIndices = bid.punk_indices;
        }
      }
      
      // If bid has specific punk indices, check if any match owned punks
      if (bidPunkIndices.length > 0) {
        return bidPunkIndices.some(id => this.ownedPunkIds.includes(id));
      }
      
      // Collection bid applies to all punks, so if user owns any punks, they can accept
      return this.ownedPunkIds.length > 0;
      
    } catch (error) {
      return false;
    }
      */
    return true;
  }

  /**
   * True when the incoming stash bid is from the same wallet as the connected/authenticated user.
   * Used to show a tooltip and grey out the Accept button.
   */
  isSameWalletStashBid = (bid: any): boolean => {
    if (!this.walletAddress || !bid?.bidder_address) {
      return false;
    }
    if ((bid as any).isLegacyBid) {
      return false;
    }
    const activeWallet = this.getActiveIdentityAddress();
    return activeWallet ? bid.bidder_address.toLowerCase() === activeWallet.toLowerCase() : false;
  };

  onAcceptStashBid(bid: any): void {
    try {
      if (bid.punksmarketBidId || bid.platform === 'punksmarket') {
        this.onAcceptIncomingPunksmarketBid(bid);
        return;
      }

      if (bid.isLegacyBid || bid.bidType === 'legacy') {
        const punkId = bid.punk_id || (bid.matchingOwnedPunks && bid.matchingOwnedPunks[0]);
        if (punkId && this.ownedPunkIds.includes(punkId)) {
          this.router.navigate(['/', 'details', punkId], {
            queryParams: { 
              acceptBid: 'true'
            }
          });
          this.closeSidebar();
          return;
        }
        console.error('No matching punk found for legacy bid');
        return;
      }
      
      // Use matchingOwnedPunks if available (precomputed), otherwise calculate
      let matchingPunkIds: number[] = [];
      
      if (bid.matchingOwnedPunks && bid.matchingOwnedPunks.length > 0) {
        matchingPunkIds = bid.matchingOwnedPunks;
      } else {
        // Fallback: calculate matching punks
        let bidPunkIndices: number[] = [];
        if (bid.punk_indices) {
          if (typeof bid.punk_indices === 'string') {
            bidPunkIndices = JSON.parse(bid.punk_indices);
          } else if (Array.isArray(bid.punk_indices)) {
            bidPunkIndices = bid.punk_indices;
          }
        }
        
        if (bidPunkIndices.length > 0) {
          // Bid targets specific punks - find which ones the user owns
          matchingPunkIds = bidPunkIndices.filter(id => this.ownedPunkIds.includes(id));
        } else {
          // Collection bid - applies to all owned punks
          matchingPunkIds = [...this.ownedPunkIds];
        }
      }
      
      if (matchingPunkIds.length === 0) {
        console.error('No matching punks found for this bid');
        return;
      }
      
      if (matchingPunkIds.length === 1) {
        // Only one punk matches - navigate directly
        this.router.navigate(['/', 'details', matchingPunkIds[0]], {
          queryParams: { 
            acceptBid: 'true',
            bidId: bid.id 
          }
        });
        this.closeSidebar();
      } else {
        // Multiple punks match - open modal to select which one
        this.selectedStashBid = bid;
        this.matchingPunkIdsForBid = matchingPunkIds;
        this.selectPunkForBidModalActive = true;
      }
    } catch (error) {
      console.error('Error processing stash bid acceptance:', error);
    }
  }

  private onAcceptIncomingPunksmarketBid(bid: any): void {
    let matchingPunkIds: number[] = bid.matchingOwnedPunks ?? [];
    if (matchingPunkIds.length === 0 && bid.punkIndices?.length) {
      matchingPunkIds = bid.punkIndices.filter((id: number) =>
        this.ownedPunkIds.includes(id),
      );
    }
    if (matchingPunkIds.length === 0) {
      console.error('[MyBids] No owned punk matches this on-chain bid');
      return;
    }

    if (matchingPunkIds.length === 1) {
      this.navigateToAcceptPunksmarketBid(matchingPunkIds[0], bid.id);
      return;
    }

    this.selectedStashBid = bid;
    this.matchingPunkIdsForBid = matchingPunkIds;
    this.selectPunkForBidModalActive = true;
  }

  private navigateToAcceptPunksmarketBid(punkId: number, bidId: string): void {
    this.router.navigate(['/', 'details', punkId], {
      queryParams: {
        acceptBid: 'true',
        bidId,
      },
    });
    this.closeSidebar();
  }

  onPunkSelected(punkId: number): void {
    if (!this.selectedStashBid) {
      return;
    }
    
    this.selectPunkForBidModalActive = false;
    // Navigate to the selected punk's item-view page
    this.router.navigate(['/', 'details', punkId], {
      queryParams: { 
        acceptBid: 'true',
        bidId: this.selectedStashBid.id 
      }
    });
    this.closeSidebar();
    // Reset
    this.selectedStashBid = null;
    this.matchingPunkIdsForBid = [];
  }

  closeSelectPunkModal(): void {
    this.selectPunkForBidModalActive = false;
    this.selectedStashBid = null;
    this.matchingPunkIdsForBid = [];
  }

  // Email modal methods
  openEmailModal(): void {
    this.emailModalActive = true;
    this.loadEmail();
  }

  closeEmailModal(): void {
    this.emailModalActive = false;
    this.userEmail = '';
  }

  async loadEmail(): Promise<void> {
    if (!this.walletAddress) return;

    this.loadingEmail = true;

    try {
      // Extract base URL from alternativeListingsGraphURI
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      // Get JWT token if available
      const jwtToken = this.stateService.getJwtToken();
      
      if (!jwtToken) {
        // User not authenticated, can't load email
        this.userEmail = '';
        this.loadingEmail = false;
        return;
      }

      // Prepare headers
      const headers: { [key: string]: string } = {
        'Authorization': `Bearer ${jwtToken}`
      };

      // Fetch email from API
      const response = await firstValueFrom(
        this.http.get<{ email: string | null }>(`${baseUrl}/api/auth/email`, { headers })
      );
      
      this.userEmail = response.email || '';
    } catch (error) {
      console.error('Error loading email:', error);
      // Don't show error to user, just log it
      this.userEmail = '';
    } finally {
      this.loadingEmail = false;
    }
  }

  async confirmSaveEmail(): Promise<void> {
    if (!this.walletAddress || !this.userEmail || !this.isValidEmail(this.userEmail)) {
      return;
    }

    if (this.loadingEmail) {
      return;
    }

    this.loadingEmail = true;

    try {
      // Extract base URL from alternativeListingsGraphURI
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      // Get JWT token if available
      const jwtToken = this.stateService.getJwtToken();
      
      if (!jwtToken) {
        throw new Error('User not authenticated');
      }

      // Prepare headers
      const headers: { [key: string]: string } = {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      };

      // Save email to API
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/email`,
          { email: this.userEmail },
          { headers }
        )
      );
      
      // Close modal on success
      this.closeEmailModal();
    } catch (error) {
      console.error('Error saving email:', error);
      // Keep modal open so user can retry
      // In a production app, you might want to show an error message
    } finally {
      this.loadingEmail = false;
    }
  }

  isValidEmail(email: string): boolean {
    if (!email || email.trim() === '') {
      return false;
    }
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  // Notifications dropdown
  toggleNotificationsMenu(): void {
    this.closeHeaderActionsMenu();
    this.notificationsMenuOpen = !this.notificationsMenuOpen;
    if (this.notificationsMenuOpen) {
      this.loadEmail();
      this.loadTelegramLinked();
    }
  }

  closeNotificationsMenu(): void {
    this.notificationsMenuOpen = false;
  }

  async loadTelegramLinked(): Promise<void> {
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const jwtToken = this.stateService.getJwtToken();
    const collectionId = environment.telegramCollectionId || 'original';
    if (!baseUrl || !jwtToken) return;

    try {
      const response = await firstValueFrom(
        this.http.get<{ linked: boolean | Record<string, boolean> }>(
          `${baseUrl}/api/auth/telegram?collectionId=${encodeURIComponent(collectionId)}`,
          { headers: { Authorization: `Bearer ${jwtToken}` } }
        )
      );
      this.telegramLinked = typeof response.linked === 'boolean' ? response.linked : !!response.linked?.[collectionId];
    } catch {
      this.telegramLinked = false;
    }
  }

  async confirmLinkTelegram(): Promise<void> {
    if (!this.telegramLinkCode?.trim() || this.loadingTelegramLink) return;

    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.loadingTelegramLink = true;
    this.telegramLinkError = null;
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/telegram/link`,
          { code: this.telegramLinkCode.trim(), collectionId: environment.telegramCollectionId || 'original' },
          { headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' } }
        )
      );
      this.telegramLinked = true;
      this.telegramLinkCode = '';
    } catch (error) {
      const msg = error instanceof HttpErrorResponse
        ? (error.error?.message ?? error.message ?? 'Failed to link Telegram')
        : (error instanceof Error ? error.message : 'Failed to link Telegram');
      this.telegramLinkError = Array.isArray(msg) ? msg[0] : msg;
      console.error('Error linking Telegram:', error);
    } finally {
      this.loadingTelegramLink = false;
    }
  }

  async confirmUnlinkTelegram(): Promise<void> {
    if (this.loadingTelegramUnlink) return;

    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const jwtToken = this.stateService.getJwtToken();
    if (!baseUrl || !jwtToken) return;

    this.loadingTelegramUnlink = true;
    try {
      await firstValueFrom(
        this.http.post<{ success: boolean }>(
          `${baseUrl}/api/auth/telegram/unlink`,
          { collectionId: environment.telegramCollectionId || 'original' },
          { headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' } }
        )
      );
      this.telegramLinked = false;
    } catch (error) {
      console.error('Error unlinking Telegram:', error);
    } finally {
      this.loadingTelegramUnlink = false;
    }
  }

  get telegramBotUrl(): string {
    return environment.telegramBotUrl || '';
  }

  @ViewChild('notificationsWrapper') notificationsWrapperRef?: ElementRef<HTMLElement>;
  @ViewChild('headerActionsMenu') headerActionsMenuRef?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.notificationsMenuOpen) {
      const notificationsWrapper = this.notificationsWrapperRef?.nativeElement;
      if (!notificationsWrapper || !notificationsWrapper.contains(target)) {
        this.closeNotificationsMenu();
      }
    }

    if (this.headerActionsMenuOpen) {
      const headerActionsMenu = this.headerActionsMenuRef?.nativeElement;
      if (!headerActionsMenu || !headerActionsMenu.contains(target)) {
        this.closeHeaderActionsMenu();
      }
    }
  }

  toggleHeaderActionsMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.headerActionsMenuOpen = !this.headerActionsMenuOpen;
    if (this.headerActionsMenuOpen) {
      this.closeNotificationsMenu();
    }
  }

  closeHeaderActionsMenu(): void {
    this.headerActionsMenuOpen = false;
  }

  openSettingsFromMenu(): void {
    this.closeHeaderActionsMenu();
    this.openSettingsModal();
  }

  refreshBidsFromMenu(): void {
    this.closeHeaderActionsMenu();
    if (!this.isConnected || !this.walletAddress) {
      return;
    }
    const activeIdentity = this.getActiveIdentityAddress();
    if (!activeIdentity) {
      return;
    }
    void this.loadBids();
    void this.loadStashInfo();
    this.initializeIncomingBids(activeIdentity);
  }

  // Settings modal methods
  openSettingsModal(): void {
    this.settingsModalActive = true;
    this.restoreDisplayOnlyHigherBidPerTokenState();
    this.restoreDisplayOnlyBidsAboveMinEthState();
  }

  closeSettingsModal(): void {
    this.settingsModalActive = false;
  }

  toggleDisplayOnlyHigherBidPerToken(): void {
    this.displayOnlyHigherBidPerToken = !this.displayOnlyHigherBidPerToken;
    // Save state to localStorage
    this.platformService.setLocalStorageItem(this.STORAGE_KEY, String(this.displayOnlyHigherBidPerToken));
    // Update BehaviorSubject to trigger reactive filtering for incoming bids only
    this.displayOnlyHigherBidPerToken$.next(this.displayOnlyHigherBidPerToken);
  }

  toggleDisplayOnlyBidsAboveMinEth(): void {
    this.displayOnlyBidsAboveMinEth = !this.displayOnlyBidsAboveMinEth;
    this.platformService.setLocalStorageItem(this.STORAGE_KEY_MIN_ETH, String(this.displayOnlyBidsAboveMinEth));
    this.displayOnlyBidsAboveMinEth$.next(this.displayOnlyBidsAboveMinEth);
    // Refetch outgoing bids from server with new filter
    if (this.isConnected && this.walletAddress) {
      this.loadBids();
    }
  }

  private restoreDisplayOnlyBidsAboveMinEthState(): void {
    if (this.platformService.isClient) {
      const savedState = this.platformService.getLocalStorageItem(this.STORAGE_KEY_MIN_ETH);
      if (savedState === 'true') {
        this.displayOnlyBidsAboveMinEth = true;
      } else if (savedState === 'false') {
        this.displayOnlyBidsAboveMinEth = false;
      }
    }
    this.displayOnlyBidsAboveMinEth$.next(this.displayOnlyBidsAboveMinEth);
  }

  private restoreDisplayOnlyHigherBidPerTokenState(): void {
    if (this.platformService.isClient) {
      const savedState = this.platformService.getLocalStorageItem(this.STORAGE_KEY);
      if (savedState === 'true') {
        this.displayOnlyHigherBidPerToken = true;
      } else if (savedState === 'false') {
        this.displayOnlyHigherBidPerToken = false;
      }
      // If savedState is null, keep default value (true)
    }
    // Update BehaviorSubject with current value
    this.displayOnlyHigherBidPerToken$.next(this.displayOnlyHigherBidPerToken);
  }

  /**
   * Filter bids to show only the highest bid per token
   * Groups collection/owner/trait bids and keeps only the highest one
   * For each token, keeps only the highest bid (comparing custom bids targeting it + collection/owner/trait bids + legacy bids)
   */
  private filterBidsByHighestPerToken(bids: IncomingBid[]): IncomingBid[] {
    if (!Array.isArray(bids) || bids.length === 0 || this.ownedPunkIds.length === 0) {
      return bids;
    }

    const bidsToKeep = new Set<IncomingBid>();

    for (const tokenId of this.ownedPunkIds) {
      let bestBid: IncomingBid | null = null;
      let bestAmount = 0;

      for (const bid of bids) {
        if (!this.bidMatchesToken(bid, tokenId)) continue;
        const amount = bid.bid_amount_eth || 0;
        if (amount > bestAmount) {
          bestAmount = amount;
          bestBid = bid;
        }
      }

      if (bestBid) {
        bidsToKeep.add(bestBid);
      }
    }

    return Array.from(bidsToKeep).sort((a, b) => {
      const amountDiff = (b.bid_amount_eth || 0) - (a.bid_amount_eth || 0);
      if (amountDiff !== 0) return amountDiff;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }

  private bidMatchesToken(bid: IncomingBid, tokenId: number): boolean {
    const bidAny = bid as any;
    if (bidAny.punksmarketBidId) {
      const matching: number[] | undefined = bidAny.matchingOwnedPunks;
      if (Array.isArray(matching) && matching.length > 0) {
        return matching.some((id: number) => Number(id) === tokenId);
      }
    }
    if (bidAny.punk_id !== undefined) return Number(bidAny.punk_id) === tokenId;
    // Collection/owner bids match all owned tokens
    if (bid.bidType === 'collection' || bid.bidType === 'owner') return true;
    // Custom/trait bids: use matchingOwnedPunks when available and complete,
    // otherwise assume match since the server already filtered by owned punks.
    const matching: any[] | undefined = bidAny.matchingOwnedPunks;
    const matchingCount: number | undefined = bidAny.matching_owned_punks_count;
    if (Array.isArray(matching) && matching.length > 0) {
      const isTruncated = matchingCount != null && matchingCount > matching.length;
      if (isTruncated) return true;
      return matching.some((id: any) => Number(id) === tokenId);
    }
    // punkIndices may be truncated (API returns first 3 + count); trust server filter
    const punkIndicesCount: number | undefined = bidAny.punk_indices_count;
    const indices: any[] = Array.isArray(bid.punkIndices) ? bid.punkIndices : [];
    if (punkIndicesCount != null && punkIndicesCount > indices.length) return true;
    if (indices.length > 0) return indices.some((id: any) => Number(id) === tokenId);
    // No indices info at all — bid was already server-filtered, assume match
    return true;
  }

}
